import {
  Box,
  Checkbox,
  FormControl,
  Input,
  InputBase,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
} from '@material-ui/core';
import { MerchantStructure } from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useState } from 'react';

import { useListPagination } from '@/components/modules/MerchantDetail/MerchantStructure/components/_shared/hooks/useListPagination';
import { AgentCard } from '@/components/modules/MerchantDetail/MerchantStructure/components/StructureCard/AgentCard';
import { Font } from '@/components/shared/components/Font';
import { IconButton } from '@/components/shared/components/IconButton';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { NewAgent } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgent';
import { SectionTitle } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/SectionTitle';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { normalizeText } from '@/utils/normalizeText';

interface AgentsSelectFormProps {
  agents: NewAgent[];
  merchantStructure: MerchantStructure;
  clonedAgentId: string;
  addAgent: (agent: NewAgent) => void;
  removeAgent: (agent: NewAgent) => void;
}

export const AgentsSelectForm = ({
  agents,
  merchantStructure,
  clonedAgentId,
  addAgent,
  removeAgent,
}: AgentsSelectFormProps) => {
  const RESET_SEARCH_ON_CLOSE_DELAY_MS = 100;

  const { typography, palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();

  const [agentOptions, setAgentOptions] = useState<NewAgent[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>(agents.map((agent) => agent.agentId));
  const [searchTerm, setSearchTerm] = useState('');
  const [isAgentSelectOpen, setIsAgentSelectOpen] = useState(false);

  const filteredAgentOptions = useMemo(() => {
    if (!searchTerm.trim()) return agentOptions;
    const term = normalizeText(searchTerm).trim().toLowerCase();
    return agentOptions.filter((agent) => {
      const name = normalizeText(agent.agentName).toLowerCase();
      const email = normalizeText(agent.agentEmail ?? '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [agentOptions, searchTerm]);

  const { pagedItems, page, rowsPerPage, onPrev, onNext, disablePrev, disableNext, itemsLength } = useListPagination({
    items: filteredAgentOptions,
    rowsPerPage: 10,
  });

  useEffect(() => {
    const agentOptions = getAgentOptions(merchantStructure, clonedAgentId);
    setAgentOptions(agentOptions);
  }, [merchantStructure, clonedAgentId]);

  useEffect(() => {
    for (const selectedAgentId of selectedAgents) {
      if (!agents.some((agent) => agent.agentId === selectedAgentId)) {
        const agentToAdd = agentOptions.find((agent) => agent.agentId === selectedAgentId);
        if (agentToAdd)
          addAgent({
            agentId: agentToAdd.agentId,
            agentName: agentToAdd.agentName,
            agentEmail: agentToAdd.agentEmail,
            channelId: agentToAdd.channelId,
            channelDescription: agentToAdd.channelDescription,
          });
      }
    }
    for (const agent of agents) {
      if (!selectedAgents.includes(agent.agentId)) {
        removeAgent(agent);
      }
    }
  }, [selectedAgents]);

  const useStyles = makeStyles(styleDefinition);
  const classes = useStyles();

  return (
    <>
      <SectionTitle titleId={'LIST_OF_AGENTS_TO_UPDATE'} textId={'LIST_OF_AGENTS_TO_UPDATE_TEXT'} />
      <FormControl style={{ width: '100%' }} className={classes.root}>
        <InputLabel id={'select-channel'}>
          <Font
            variant={'body2'}
            color={'textSecondary'}
            style={{
              textDecoration: 'underline',
              fontSize: `${typography.body2.fontSize} !important`,
              cursor: 'pointer',
              display: 'inline-block',
              padding: '8px 0',
            }}
            onClick={() => setIsAgentSelectOpen(true)}
          >
            <Text tId={'SELECT_DESIRED_AGENTS'} />
          </Font>
        </InputLabel>
        <Select
          multiple
          labelId={'select-channel'}
          id={'select-channel'}
          open={isAgentSelectOpen}
          onOpen={() => setIsAgentSelectOpen(true)}
          value={selectedAgents}
          onChange={(e) => setSelectedAgents(e.target.value as string[])}
          onClose={() => {
            setIsAgentSelectOpen(false);
            setTimeout(() => setSearchTerm(''), RESET_SEARCH_ON_CLOSE_DELAY_MS);
          }}
          input={<Input />}
          MenuProps={{
            getContentAnchorEl: null,
            PaperProps: { style: { width: 640, maxWidth: '90vw' } },
            MenuListProps: { style: { overflowX: 'auto', maxWidth: 640 } },
          }}
          renderValue={(selectedAgentIds) => {
            const selectedAgentIdsSafe = selectedAgentIds as string[];
            const agents = agentOptions.filter((agent) => selectedAgentIdsSafe.includes(agent.agentId));
            return (
              <>
                {agents.map((agent) => (
                  <AgentCard key={agent.agentId} agent={agent} />
                ))}
              </>
            );
          }}
        >
          {agentOptions.length === 0 ? (
            <MenuItem value={undefined} disabled>
              <Text tId={'NO_OPTIONS_TO_DISPLAY'} />
            </MenuItem>
          ) : itemsLength === 0 && searchTerm.trim() ? (
            <Box px={1} py={0.5}>
              <PendingDataWarning tId={'NO_AGENTS_FOUND_BY_SEARCH_TERM'} />
            </Box>
          ) : (
            pagedItems.map((agent) => (
              <MenuItem key={agent.agentId} value={agent.agentId}>
                <Checkbox checked={selectedAgents.indexOf(agent.agentId) > -1} />
                <ListItemText
                  primary={
                    <Box display={'flex'} alignItems={'center'} style={{ whiteSpace: 'nowrap' }}>
                      <i className={'material-icons-outlined'} style={{ fontSize: 20, marginRight: 8 }}>
                        storefront
                      </i>
                      <Font variant={'body2'}>{agent.channelDescription}</Font>
                      <i className={'material-icons-outlined'} style={{ fontSize: 20, marginLeft: 16 }}>
                        arrow_forward
                      </i>
                      <i className={'material-icons-outlined'} style={{ fontSize: 20, marginLeft: 16, marginRight: 8 }}>
                        account_circle
                      </i>
                      <Font variant={'body2'}>{agent.agentName}</Font>
                    </Box>
                  }
                />
              </MenuItem>
            ))
          )}
          {agentOptions.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              pl={'28px'}
              pr={1}
              flexWrap="wrap"
              style={{ backgroundColor: palette.grey[100] }}
            >
              <Font variant={'caption'} color={'textSecondary'} style={{ flex: '1 1 120px', minWidth: 0 }}>
                <InputBase
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  classes={{ root: classes.searchInputRoot }}
                  placeholder={`${getTextIdTranslation('SEARCH_AGENTS')}`}
                  style={{ width: '100%', marginRight: 8 }}
                  startAdornment={
                    <i
                      className="material-icons-outlined mr-2"
                      style={{ verticalAlign: 'text-top', fontSize: '22px', color: palette.text.secondary }}
                    >
                      search
                    </i>
                  }
                />
              </Font>
              {!!itemsLength && (
                <Box display="flex" alignItems="center">
                  <Font variant="caption" style={{ color: palette.text.secondary }}>
                    {`${(page - 1) * rowsPerPage + 1}-${
                      page * rowsPerPage > itemsLength ? itemsLength : page * rowsPerPage
                    } ${getTextIdTranslation('OF')} ${itemsLength}`}
                  </Font>
                  <IconButton icon="chevron_left" size="small" onClick={onPrev} disabled={disablePrev} />
                  <IconButton icon="chevron_right" size="small" onClick={onNext} disabled={disableNext} />
                </Box>
              )}
            </Box>
          )}
        </Select>
      </FormControl>
    </>
  );
};

const getAgentOptions = (merchantStructure: MerchantStructure, agentId: string): NewAgent[] => {
  return merchantStructure.merchant.channels.reduce((cum, channel) => {
    const channelAgents = channel.agents
      .filter((agent) => agent.id !== agentId)
      .map((agent) => ({
        agentId: agent.id,
        agentName: agent.name,
        agentEmail: agent.username,
        channelId: channel.id,
        channelDescription: channel.description,
      })) as NewAgent[];
    return [...cum, ...channelAgents];
  }, [] as NewAgent[]);
};

const styleDefinition = {
  root: {
    margin: 0,
    minHeight: '200px',
    '& .MuiSelect-select.MuiSelect-select': { paddingRight: 0, position: 'absolute', top: '10px' },
    '& .MuiInput-underline.Mui-disabled:before': {
      borderBottomColor: 'none',
      borderBottom: 'none',
      border: '0px',
    },
    '& .MuiInput-underline:before': {
      border: '0px',
    },
    '& .MuiInput-underline:after': {
      border: '0px',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottomWidth: 0,
      border: '0px',
    },
    '& .MuiSelect-icon': {
      // top: 0,
      // marginTop: '-18px',
      display: 'none',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(0) scale(1)',
    },
  },
  searchInputRoot: {
    '& .MuiInputBase-input': {
      fontSize: 'inherit',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  },
};
